import { formatAddressForDisplay } from '../utils/address';
import { shouldApplyAutomaticQuoteReference } from './project-reference';
import { isTransportSender, isUnchangedHeaderSeed } from './stage1-header-seeds';

export interface AutomaticExtractionOptions {
    todayDefault?: string | null;
}

/** Contact extraction is additive in both attended import and pre-Summary preparation. */
export function selectAutomaticContactExtractionUpdates(
    formData: Record<string, unknown>,
    extracted: Record<string, unknown>,
    options: AutomaticExtractionOptions = {},
): Record<string, string> {
    const candidates: Record<string, string> = {};
    const fields: Record<string, string> = {
        address: 'address', strataPlan: 'strataplan', orderNumber: 'orderNumber',
        clientName: 'clientName', clientCompany: 'clientCompany', clientEmail: 'clientEmail',
        clientPhone: 'clientPhone', clientTitle: 'clientTitle', siteContactName: 'siteContactName',
        siteContactPhone: 'siteContactPhone', jobDescription: 'jobDescription',
        projectDescription: 'jobDescription', scopeObjective: 'scopeObjective',
        specialInstructions: 'specialInstructions', visitDate: 'visitDate',
    };
    for (const [source, target] of Object.entries(fields)) {
        const value = extracted?.[source];
        if (typeof value !== 'string') continue;
        let text = value.trim();
        if (!text || /^(?:null|n\/?a|not provided|not found|not specified|unknown|none)$/i.test(text)) continue;
        if (target === 'address') text = formatAddressForDisplay(text);
        if (target === 'strataplan') text = text.replace(/^strata\s*plan\s*/i, 'SP').replace(/^sp\s+/i, 'SP');
        if (target === 'orderNumber') text = text.replace(/^Q\.?R\.?\s*/i, '').replace(/^purchase\s*order\s*/i, 'PO-').replace(/^work\s*order\s*/i, 'WO-').replace(/^job\s*no\.?\s*/i, 'Job ');
        if (target === 'visitDate' && (text.length < 4 || text.length > 30 || !/\d/.test(text))) continue;
        if (text && candidates[target] == null) candidates[target] = text;
    }
    return selectAutomaticExtractionUpdates(formData, candidates, options) as Record<string, string>;
}

export function isAutomaticExtractionFieldAvailable(
    formData: Record<string, unknown> | null | undefined,
    fieldName: string,
    { todayDefault = null }: AutomaticExtractionOptions = {},
): boolean {
    if (fieldName === 'strataplan' && [formData?.strataPlan, formData?.strata_plan].some(value => typeof value === 'string' && value.trim())) return false;
    if (fieldName === 'orderNumber' && (
        !shouldApplyAutomaticQuoteReference(formData?.orderNumber)
        || formData?.orderNumberConfirmed === true
        || [formData?.userConfirmedOrderNumber, formData?.confirmedOrderNumber].some(value => typeof value === 'string' && value.trim())
    )) return false;
    const value = formData?.[fieldName];
    if (value == null || String(value).trim() === '') return true;
    // A client field the email header seeded and nobody has touched is the
    // machine's value, not the operator's: verified extraction may replace it. An
    // edited seed, and any value with no seed record, is left alone.
    if (isUnchangedHeaderSeed(formData, fieldName)) return true;
    return fieldName === 'visitDate'
        && todayDefault != null
        && String(value).trim() === String(todayDefault).trim();
}

export function selectAutomaticExtractionUpdates(
    formData: Record<string, unknown> | null | undefined,
    candidateUpdates: Record<string, unknown> | null | undefined,
    options: AutomaticExtractionOptions = {},
): Record<string, unknown> {
    return Object.fromEntries(
        Object.entries(candidateUpdates || {}).filter(([fieldName]) => (
            isAutomaticExtractionFieldAvailable(formData, fieldName, options)
        )),
    );
}

export function selectAutomaticVerifierCorrections(
    currentFormData: Record<string, unknown> | null | undefined,
    candidateCorrections: Record<string, unknown> | null | undefined,
    deterministicValues: Record<string, unknown> | null | undefined,
    initiallyWritableFields: Iterable<string> | Set<string> | string[] | null | undefined,
): Record<string, unknown> {
    const canWrite = initiallyWritableFields instanceof Set
        ? initiallyWritableFields
        : new Set(initiallyWritableFields || []);

    return Object.fromEntries(
        Object.entries(candidateCorrections || {}).filter(([fieldName, value]) => {
            if (!canWrite.has(fieldName) || value == null || String(value).trim() === '') {
                return false;
            }
            const currentValue = currentFormData?.[fieldName];
            if (currentValue == null || String(currentValue).trim() === '') return true;
            // An unchanged header seed is replaceable; an edited one is not.
            if (isUnchangedHeaderSeed(currentFormData, fieldName)) return true;
            const deterministicValue = deterministicValues?.[fieldName];
            return deterministicValue != null
                && String(currentValue) === String(deterministicValue);
        }),
    );
}

/**
 * The fields the classification verifier reviews after a Quote Request is
 * read (classification-verifier.js verifyClassification). The requester's
 * email rides along so the email-domain rule (applyEmailDomainCanonical) can
 * name the strata firm the address belongs to. Before, this input had no
 * email and that rule never ran (C5). The email the extraction found wins;
 * the job's own requester email (set from an email's From line) is next.
 */
export function buildClassificationVerifierInput(
    extracted: Record<string, any> | null | undefined,
    formData: Record<string, unknown> | null | undefined = null,
): Record<string, string> {
    const text = (value: unknown) => (typeof value === 'string' ? value.trim() : '');
    return {
        clientName: extracted?.clientName || '',
        clientCompany: extracted?.clientCompany || '',
        // A delivery or no-reply sender's address names the platform, not the firm:
        // never let it drive the email-domain rule. (Projects seeded before the
        // header seeds were filtered can still carry one.)
        clientEmail: text(extracted?.clientEmail)
            || (isTransportSender(text(formData?.clientEmail)) ? '' : text(formData?.clientEmail)),
        siteInspector: extracted?.assessorName || extracted?.siteInspector || '',
        siteContactName: extracted?.siteContactName || '',
        address: extracted?.address || '',
        scope: extracted?.scope || extracted?.jobDescription || '',
    };
}
